import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {

  private readonly idleTimeMs = 30 * 60 * 1000; // 30 min
  private readonly warningTimeMs = 30 * 1000;

  private idleTimerSub?: Subscription;
  private countdownSub?: Subscription;
  private isCountdownRunning = false;
  private listenersAdded = false;

  showWarning$ = new BehaviorSubject<boolean>(false);
  countdown$ = new BehaviorSubject<number>(30);

  constructor(
    private router: Router,
    private ngZone: NgZone
  ) {}

  start(): void {
    if (!this.isLoggedIn()) {
      this.stop();
      return;
    }

    if (!this.listenersAdded) {
      this.listenForActivity();
      this.listenersAdded = true;
    }

    this.isCountdownRunning = false;
    this.resetTimer();
  }

  stop(): void {
    this.idleTimerSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
    this.isCountdownRunning = false;
    this.showWarning$.next(false);
  }

  resetTimer(): void {
    if (!this.isLoggedIn()) {
      this.stop();
      return;
    }

    if (this.isCountdownRunning) {
      return;
    }

    this.idleTimerSub?.unsubscribe();
    this.countdownSub?.unsubscribe();

    this.showWarning$.next(false);
    this.countdown$.next(this.warningTimeMs / 1000);

    this.idleTimerSub = timer(this.idleTimeMs - this.warningTimeMs)
      .subscribe(() => this.startCountdown());
  }

  stayLoggedIn(): void {
    if (!this.isLoggedIn()) {
      this.stop();
      return;
    }

    this.idleTimerSub?.unsubscribe();
    this.countdownSub?.unsubscribe();

    this.isCountdownRunning = false;
    this.showWarning$.next(false);

    this.resetTimer();
  }

  private startCountdown(): void {
    if (!this.isLoggedIn()) {
      this.stop();
      return;
    }

    this.idleTimerSub?.unsubscribe();

    this.isCountdownRunning = true;
    this.showWarning$.next(true);

    let secondsLeft = this.warningTimeMs / 1000;
    this.countdown$.next(secondsLeft);

    this.countdownSub = timer(1000, 1000).subscribe(() => {
      secondsLeft--;
      this.countdown$.next(secondsLeft);

      if (secondsLeft <= 0) {
        this.logout();
      }
    });
  }

  private listenForActivity(): void {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(eventName => {
      window.addEventListener(eventName, () => {
        if (!this.isCountdownRunning && this.isLoggedIn()) {
          this.ngZone.run(() => this.resetTimer());
        }
      });
    });
  }

  logout(): void {
  this.stop();

  // Tell route guard to bypass unsaved changes popup
  sessionStorage.setItem('isLoggingOut', 'true');

  // Clear login/session data except the flag
  sessionStorage.removeItem('companyId');
  sessionStorage.removeItem('email');
  sessionStorage.removeItem('roleCode');
  sessionStorage.removeItem('companyName');
  sessionStorage.removeItem('username');
  sessionStorage.removeItem('superAdminCompanyId');

  localStorage.clear();

  this.router.navigate(['']);
}

  private isLoggedIn(): boolean {
    const companyId = sessionStorage.getItem('companyId');
    return !!companyId && companyId !== 'null' && companyId !== 'undefined';
  }
}